import React from 'react';
import { arrayOf, number, oneOfType, shape, string } from 'prop-types';
import { Box, Typography } from '@mui/material';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import { styled } from '@mui/material/styles';
import OrderSummary from '../order-summary/OrderSummary';

const CheckIcon = styled(CheckCircleOutlineOutlinedIcon)(({ theme }) => ({
  color: theme.palette.secondary.main,
  fontSize: 120,
}));

const ErrorIcon = styled(CancelOutlinedIcon)(({ theme }) => ({
  color: theme.palette.error.main,
  fontSize: 120,
}));

const OrderConfirmation = styled(Box)({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
  padding: '10px',
  textAlign: 'center',
  width: '100%',
});

const PtoNumber = styled(Typography)({
  fontSize: 36,
  fontWeight: 'bold',
  margin: 4,
});

const PtoNumberWrapper = styled(Box)({
  flexDirection: 'column',
  margin: 8,
});

const PtoTitle = styled(Typography)({
  margin: '2px 0',
  textTransform: 'uppercase',
});

const SectionDiv = styled(Box)({
  alignItems: 'center',
  flexDirection: 'column',
  flexGrow: 'unset',
  justifyContent: 'center',
  width: '100%',
});

const SubTitle = styled(Typography)({
  margin: '5px 0',
});

const Title = styled(Typography)({
  fontWeight: 'bold',
  margin: '5px 0',
  textTransform: 'uppercase',
});

const OrderComplete = ({
  affiliateOptions,
  affiliates,
  currentAffiliate,
  currentLocation,
  currentProduct,
  chainContactIds,
  data,
  originatorLocationProductOrigins,
  submitError,
}) => {
  const modifiedContactIds = [];
  // on a copy of a pto, affiliates will load after chainContactIds. need to check both.
  if (chainContactIds) {
    chainContactIds.forEach(id => {
      const chainContact = affiliateOptions.find(({ value }) => value === id.toString());
      const name = chainContact.label;
      modifiedContactIds.push({ id, name });
    });
  }

  return (
    <SectionDiv display="flex" flexGrow={1} flexShrink={0}>
      <OrderConfirmation>
        <Box display="flex">{submitError ? <ErrorIcon /> : <CheckIcon />}</Box>
        <Title variant="h6">{submitError ? 'Error Placing Order' : 'Order Received'}</Title>
        {!submitError && (
          <>
            <SubTitle variant="body1">
              Your PTO has been received and sent to {data.response.recipient} for approval.
            </SubTitle>
            <PtoNumberWrapper display="flex">
              <PtoTitle variant="subtitle1">PTO Number</PtoTitle>
              <PtoNumber variant="h6">{data.response.ptoNumber}</PtoNumber>
            </PtoNumberWrapper>
          </>
        )}
      </OrderConfirmation>
      <OrderSummary
        chainContactIds={modifiedContactIds}
        createPtoData={data}
        currentAffiliate={currentAffiliate}
        currentLocation={currentLocation}
        currentProduct={currentProduct}
        detailsExpanded
        headerButton={false}
        headerTitle="Order Summary"
        originatorLocationProductOrigins={originatorLocationProductOrigins}
        userAffiliates={affiliates}
      />
    </SectionDiv>
  );
};

OrderComplete.propTypes = {
  affiliateOptions: arrayOf(shape({})).isRequired,
  affiliates: arrayOf(shape({})).isRequired,
  chainContactIds: arrayOf(number),
  currentAffiliate: shape({}),
  currentLocation: shape({}),
  currentProduct: shape({}),
  data: shape({}).isRequired,
  originatorLocationProductOrigins: arrayOf(shape({})).isRequired,
  submitError: oneOfType([string, shape({})]).isRequired,
};
OrderComplete.defaultProps = {
  chainContactIds: [],
  currentAffiliate: {},
  currentLocation: {},
  currentProduct: {},
};

export default OrderComplete;
